import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { H2, Table, Tile, Title } from '@procore/core-react';
import { IRating } from '../../app/interfaces/IRating';
import { IQueryParams, ISortableHeader } from '../../app/interfaces/ISortableTable';
import { ProcoreLinkCell, ProcoreTextCell } from '../tables/ProcoreTableCells';
import SortableTable from '../tables/SortableTable';
import PageHeader from '../commonComponents/PageHeader';
import { getProjectRatingsThunk, selectProjectRatings, selectProjectRatingsState } from '../../slices/projectRatingsSlice';

const ProjectRatings: React.FC = () => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const companyId = parseInt(params.companyId!);
  const projectId = parseInt(params.projectId!);
  const { queryState } = useAppSelector(selectProjectRatingsState);

  const headers: ISortableHeader[] = [
    { value: 'vendor', key: 'vendorName', text: 'Vendor', sortable: false },
    { value: 'rating', key: 'rating', text: 'Rating', sortable: true },
    { value: 'commitment', key: 'commitmentName', text: 'Commitment', sortable: false },
    { value: 'created_date', key: 'createdDate', text: 'Created', sortable: true },
    { value: 'created_by', key: 'createdByLastName', text: 'By', sortable: false },
  ];

  const queryData = (queryParams?: IQueryParams) =>
    dispatch(getProjectRatingsThunk({ companyId, projectId, queryParams }));

  const renderCard = (item: any) => {
    const r = item as IRating;
    return (
      <Tile orientation="portrait">
        <Tile.Content>
          <Title>
            <Title.Text>
              <H2>
                <Link to={`/companies/${companyId}/vendors/${r.vendorId}`}>{r.vendorName}</Link>
              </H2>
            </Title.Text>
            <Title.Subtext>{r.commitmentName ?? 'Unspecified commitment'}</Title.Subtext>
          </Title>
          <div>Rating: {r.rating}</div>
          {r.description && <div>{r.description}</div>}
        </Tile.Content>
      </Tile>
    );
  };

  const renderRow = (item: any) => {
    const r = item as IRating;
    return (
      <React.Fragment key={r.id}>
        <Table.BodyRow>
          <ProcoreLinkCell to={`/companies/${companyId}/vendors/${r.vendorId}`} cta={r.vendorName ?? 'Vendor'} />
          <ProcoreTextCell text={String(r.rating)} />
          <ProcoreTextCell text={r.commitmentName ?? ''} />
          <ProcoreTextCell text={new Date(r.createdDate).toLocaleDateString()} />
          <ProcoreTextCell text={`${r.createdByFirstName} ${r.createdByLastName}`} />
        </Table.BodyRow>
      </React.Fragment>
    );
  };

  return (
    <div id="project-ratings">
      <PageHeader title="Project Ratings" />
      <SortableTable
        dataSelector={selectProjectRatings}
        headers={headers}
        renderDataCard={renderCard}
        renderDataRow={renderRow}
        queryState={queryState}
        queryData={queryData}
      />
    </div>
  );
};

export default ProjectRatings;